import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography,
  CardMedia,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Button,
  Stack
} from '@mui/material';
import { 
  NavigateNext, 
  NavigateBefore,
  FirstPage,
  LastPage 
} from '@mui/icons-material';
import scenarios from '../data/scenarios.json';
import { useNavigate, useLocation } from 'react-router-dom';

const TrainingScenarios = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const navigate = useNavigate();
  const location = useLocation();
  const [filteredScenarios, setFilteredScenarios] = useState([]);
  const defaultCategory = '商業';
  
  // Move getCategory outside useEffect and wrap in useMemo
  const getCategory = React.useMemo(() => {
    return () => {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get('category') || defaultCategory;
    };
  }, [location.search]);

  // Filter scenarios based on category
  useEffect(() => {
    const currentCategory = getCategory();
    const filtered = scenarios.filter(scenario => scenario.category === currentCategory);
    setFilteredScenarios(filtered);
    setCurrentPage(1);
  }, [getCategory]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredScenarios.length / itemsPerPage);

  // Get current scenarios for the page
  const getCurrentScenarios = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredScenarios.slice(startIndex, endIndex);
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Adjust visible page numbers based on screen size and total pages
  const getPageNumbers = () => {
    if (totalPages <= 1) return [1];
    
    if (isMobile) {
      // Show current page and last page only on mobile
      return currentPage === totalPages ? 
        [totalPages - 1, totalPages] : 
        [currentPage, currentPage < totalPages - 1 ? currentPage + 1 : totalPages];
    } else if (isTablet) {
      // Show first page, current/adjacent page, and last page on tablet
      const numbers = [1];
      if (currentPage > 2) numbers.push('...');
      if (currentPage !== 1 && currentPage !== totalPages) numbers.push(currentPage);
      if (currentPage < totalPages - 1) numbers.push('...');
      if (totalPages !== 1) numbers.push(totalPages);
      return numbers;
    }
    
    // Desktop view
    const numbers = [1];
    if (currentPage > 2) numbers.push('...');
    if (currentPage !== 1 && currentPage !== totalPages) numbers.push(currentPage);
    if (currentPage < totalPages - 1) numbers.push('...');
    if (totalPages !== 1) numbers.push(totalPages);
    return numbers;
  };

  const handleCardClick = (scenario) => {
    setSelectedScenario(scenario);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedScenario(null);
  };

  const handleStartPractice = (id) => {
    handleClose();
    // Check if id is already in 'caseX' format
    const scenarioKey = id.toString().startsWith('case') ? id : `case${id}`;
    navigate(`/chat/${scenarioKey}`);
  };

  // Update breadcrumb to show current category
  const categoryLabel = getCategory() === '商業' ? '理專' : '電銷';

  return (
    <Box 
      sx={{ 
        p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
        backgroundColor: '#fff', 
        minHeight: '100vh'
      }}
    >
      {/* Updated Header with category */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 2, sm: 3, md: 4 }, // Responsive margin
          flexWrap: 'wrap'
        }}
      >
        <Typography 
          sx={{ 
            color: '#666',
            fontSize: { xs: '0.875rem', sm: '1rem' }, // Responsive font size
            '& span': {
              color: '#999',
              mx: 0.5,
            }
          }}
        >
          Fubon <span>/</span> {categoryLabel} <span>/</span> 陪練課程
        </Typography>
      </Box>

      {/* Grid of cards */}
      <Grid 
        container 
        spacing={{ xs: 2, sm: 2, md: 3 }} // Responsive grid spacing
      >
        {getCurrentScenarios().map((scenario) => (
          <Grid 
            item 
            xs={12}          // 1 card per row on mobile
            sm={6}           // 2 cards per row on tablet
            md={4}           // 3 cards per row on small desktop
            lg={3}           // 4 cards per row on large desktop
            key={scenario.id}
          >
            <Card 
              onClick={() => handleCardClick(scenario)}
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderRadius: { xs: 1, sm: 2 }, // Responsive border radius
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }
              }}
            >
              <CardMedia
                component="img"
                height={{ xs: '180', sm: '200' }} // Responsive image height
                image={scenario.image}
                alt={scenario.name}
                sx={{
                  objectFit: 'cover',
                }}
              />
              <CardContent 
                sx={{ 
                  p: { xs: 1.5, sm: 2 } // Responsive padding
                }}
              >
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 500,
                    mb: 0.5,
                    fontSize: { xs: '0.9rem', sm: '1rem' } // Responsive font size
                  }}
                >
                  {scenario.name}，{scenario.age}
                  <Box component="span" sx={{ 
                    ml: 1, 
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    color: 'text.secondary',
                    fontWeight: 400,
                    display: 'inline-block'
                  }}>
                    {scenario.difficulty === "easy" ? "初級" : 
                     scenario.difficulty === "medium" ? "中級" : 
                     scenario.difficulty === "hard" ? "高級" : "初級"}
                  </Box>
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }, // Responsive font size
                    lineHeight: 1.5
                  }}
                >
                  {scenario.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Show message if no scenarios found */}
      {filteredScenarios.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
          此分類沒有可用的案例
        </Typography>
      )}

      {/* Scenario Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        {selectedScenario && (
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  component="img"
                  src={selectedScenario.image}
                  alt={selectedScenario.name}
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    objectFit: 'cover'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {selectedScenario.name}，{selectedScenario.age}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    難度：{selectedScenario.difficulty === "easy" ? "初級" : 
                              selectedScenario.difficulty === "medium" ? "中級" : 
                              selectedScenario.difficulty === "hard" ? "高級" : "初級"}
                  </Typography>
                  <Typography variant="body1">
                    {selectedScenario.title}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" sx={{ my: 2 }}>
                {selectedScenario.description.split('\\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < selectedScenario.description.split('\\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>

              <Stack 
                direction="row" 
                spacing={2} 
                sx={{ mt: 2 }}
              >
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={handleClose}
                  sx={{ borderRadius: 2 }}
                >
                  建議手冊
                </Button>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={() => handleStartPractice(selectedScenario.id)}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#00a3a3',
                    '&:hover': {
                      backgroundColor: '#008080'
                    }
                  }}
                >
                  開始練習
                </Button>
              </Stack>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Updated Pagination */}
      {filteredScenarios.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 3, sm: 4 },
            mb: { xs: 2, sm: 2 },
            gap: { xs: 0.5, sm: 1 },
            flexWrap: 'wrap'
          }}
        >
          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' },
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            <FirstPage fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <NavigateBefore fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          {getPageNumbers().map((page, index) => (
            <Box
              key={index}
              onClick={() => page !== '...' && handlePageChange(page)}
              sx={{
                width: { xs: 24, sm: 28 },
                height: { xs: 24, sm: 28 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                cursor: page === '...' ? 'default' : 'pointer',
                backgroundColor: page === currentPage ? '#2196f3' : 'transparent',
                color: page === currentPage ? '#fff' : '#666',
                '&:hover': {
                  backgroundColor: page === currentPage ? '#2196f3' : 
                                page === '...' ? 'transparent' : '#f5f5f5'
                },
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {page}
            </Box>
          ))}

          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <NavigateNext fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <IconButton 
            size={isMobile ? "small" : "medium"}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' },
              display: { xs: 'none', sm: 'flex' }
            }}
          >
            <LastPage fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default TrainingScenarios; 